import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auto-generated default affirmations
const AUTO_GENERATED_AFFIRMATIONS = [
  "I am a powerful creator of my reality",
  "My consciousness expands with every breath", 
  "I embody wisdom and authentic expression",
  "I am connected to infinite possibilities",
  "My presence creates positive transformation",
  "I trust my inner guidance completely",
  "I attract abundance in all forms",
  "My mind is clear and focused",
  "I radiate love and compassion",
  "I am becoming my highest self"
];

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1); // 1: Registration, 2: FAQ, 3: Affirmations
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [affirmations, setAffirmations] = useState([...AUTO_GENERATED_AFFIRMATIONS]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleAffirmationChange = (index, value) => {
    const newAffirmations = [...affirmations];
    newAffirmations[index] = value;
    setAffirmations(newAffirmations);
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Move to FAQ step
    setStep(2);
  };

  const handleFinalRegistration = async () => {
    setLoading(true);
    setError('');

    try {
      // Register user with affirmations
      const registrationData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        selected_affirmations: affirmations
      };

      const response = await axios.post(`${API}/auth/register`, registrationData);
      
      // Auto-login after registration
      await login(formData.username, formData.password);
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div 
            className="inline-flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity mb-6"
            onClick={() => navigate('/')}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">🧠</span>
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Modern Human Being
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Begin Your Journey</h1>
          <p className="text-gray-600">
            Step {step} of 3: {step === 1 ? 'Account Details' : step === 2 ? 'FAQ' : 'Your Top 10 Affirmations'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 1 ? (
            /* Step 1: Registration Form */
            <form onSubmit={handleStep1Submit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Username *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Choose a unique username"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Enter your email address"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Create a secure password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-semibold hover:shadow-lg transition-shadow"
              >
                Continue to FAQ ✨
              </button>
            </form>
          ) : step === 2 ? (
            /* Step 2: FAQ Page (Blank for now) */
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
                  <div className="text-6xl mb-4">❓</div>
                  <p className="text-gray-600 mb-4">
                    FAQ content will be added here soon.
                  </p>
                  <p className="text-sm text-gray-500">
                    This section will contain helpful information about your consciousness journey.
                  </p>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-semibold hover:shadow-lg transition-shadow"
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            /* Step 3: Top 10 Affirmations (Auto-generated but editable) */
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Your Top 10 Affirmations</h2>
                <p className="text-gray-600 mb-4">
                  We've auto-generated 10 powerful affirmations for you. You can edit any of them to match your personal journey. 
                  These can be changed later in your Profile settings.
                </p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <p className="text-emerald-700 text-sm">
                    💡 <strong>Note:</strong> You can change these affirmations and add vision board images anytime in your Profile Settings.
                  </p>
                </div>
              </div>

              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {affirmations.map((affirmation, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <span className="text-emerald-600 font-semibold mt-3 text-sm">{index + 1}.</span>
                    <textarea
                      value={affirmation}
                      onChange={(e) => handleAffirmationChange(index, e.target.value)}
                      className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                      rows="2"
                      placeholder={`Affirmation ${index + 1}`}
                    />
                  </div>
                ))}
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleFinalRegistration}
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Account...' : 'Complete Registration 🙏'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <p className="text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-emerald-600 hover:text-emerald-700 font-semibold"
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;